import { useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, LinearProgress } from '@mui/material';
import { stripDiacritics } from '@realadvisor/transform/strip-diacritics';
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { UserBuyerLeadKanbanItemFragment } from '@graphql/__generated__/graphql';
import { BuyerLeadEditDrawer } from '@modules/_shared/components/buyer-lead/BuyerLeadEditDrawer';
import {
  buyerLeadKanbanReorderingDisabled,
  createBuyerLeadKanbanColumnDefinitions,
  getBuyerLeadKanbanLiveRegionAnnouncement,
  getBuyerLeadKanbanStages,
  getBuyerLeadStageId,
  hasBuyerLeadKanbanStageChanged,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { KanbanBoard } from '@modules/_shared/components/kanban/KanbanBoard';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { DrawerContainerProvider } from '@modules/_shared/hooks/drawerContainerProvider';
import { subscribeFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { isNullOrEmpty } from '@modules/_shared/utils/strings';
import { UserBuyerLeadActionToolbar } from '@modules/users/components/UserBuyerLeadActionToolbar';
import { UserBuyerLeadDnDColumn } from '@modules/users/components/UserBuyerLeadDnDColumn';
import { UserBuyerLeadMatchingDrawer } from '@modules/users/components/UserBuyerLeadMatchingDrawer';

const NO_DRAWER_CONTAINER: { current: HTMLDivElement | null } = {
  current: null,
};

export const GET_USER_BUYERS_LEAD = gql(/* GraphQL */ `
  query GetUserBuyersLead($id: uuid!) {
    lead_stages(
      where: {
        pipeline: { lead_type: { _eq: "buyer" }, name: { _eq: "house_sales" } }
      }
      order_by: { order_nr: asc }
    ) {
      id
      name
      label
      status
    }
    buyer_leads(
      where: { user_id: { _eq: $id } }
      order_by: [{ lead_stage: { order_nr: asc } }, { updated_at: desc }]
    ) {
      ...UserBuyerLeadKanbanItem
      ...UserBuyerLeadActionToolbarBuyerLead
    }
    property_searches(
      where: { user_id: { _eq: $id }, notification: { _neq: "never" } }
    ) {
      ...ListingsMatchingFiltersPropertySearches
    }
  }
`);

export const UPDATE_LEAD_USER_STAGE = gql(/* GraphQL */ `
  mutation UpdateBuyerLeadUserStage(
    $id: uuid!
    $updates: buyer_leads_set_input!
  ) {
    update_buyer_leads_by_pk(pk_columns: { id: $id }, _set: $updates) {
      id
      ...UserBuyerLeadKanbanItem
      ...UserBuyerLeadActionToolbarBuyerLead
    }
  }
`);

export const UserBuyersLeadKanban: React.FC<{ userId?: string }> = ({
  userId,
}) => {
  const { userId: userIdParam } = useParams();
  const id = userId ?? userIdParam;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, error, loading, refetch } = useQuery(GET_USER_BUYERS_LEAD, {
    variables: { id: id ?? '' },
    skip: id == null,
  });

  useEffect(() => {
    const unsubscribe = subscribeFeedRefresh(() => {
      void refetch();
    });
    return unsubscribe;
  }, [refetch]);

  const [filter, setFilter] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isSelectionModeForced, setIsSelectionModeForced] = useState(false);
  const [updateOrderError, setUpdateOrderError] = useState<ErrorLike | null>(
    null,
  );

  const [updateLeadStage] = useMutation(UPDATE_LEAD_USER_STAGE, {
    refetchQueries: [GET_USER_BUYERS_LEAD],
    onError: setUpdateOrderError,
  });

  const buyerLeads = useMemo(() => {
    if (isNullOrEmpty(filter, true)) {
      return data?.buyer_leads ?? [];
    }

    const needle = stripDiacritics(filter).toLowerCase();

    return (
      data?.buyer_leads.filter(lead =>
        stripDiacritics(
          lead.lot.title ?? formatAddress(lead.lot.property, ', '),
        )
          .toLowerCase()
          .includes(needle),
      ) ?? []
    );
  }, [data?.buyer_leads, filter]);

  const initialSearchParams = useRef(searchParams);
  const columnsDefinition = useMemo(
    () =>
      createBuyerLeadKanbanColumnDefinitions<UserBuyerLeadKanbanItemFragment>(
        data?.lead_stages,
        ({ index, column, items }) => (
          <UserBuyerLeadDnDColumn
            key={column.id}
            index={index}
            column={column}
            items={items}
            onItemClicked={lead => {
              navigate({
                pathname: `./buyers/${lead.id}`,
                search: initialSearchParams.current.toString(),
              });
            }}
          />
        ),
      ),
    [data?.lead_stages, navigate],
  );

  const matchedStageId = getBuyerLeadStageId(data?.lead_stages, 'matched');

  const matchSentStageId = getBuyerLeadStageId(data?.lead_stages, 'match_sent');

  const navigateBack = () =>
    navigate({
      pathname: '.',
      search: initialSearchParams.current.toString(),
    });

  if (id == null) {
    return null;
  }

  if (error != null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <>
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      )}
      {data?.buyer_leads != null && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <UserBuyerLeadActionToolbar
            propertySearches={data.property_searches}
            buyerLeads={buyerLeads}
            onFilterChange={setFilter}
            selectedBuyerLeads={selectedItems}
            onReset={() => {
              setSelectedItems([]);
              setIsSelectionModeForced(false);
            }}
            toggleSelectionMode={() => setIsSelectionModeForced(prev => !prev)}
            forceSelectionMode={isSelectionModeForced}
            stages={{
              matched: matchedStageId,
              matchSent: matchSentStageId,
            }}
            columns={getBuyerLeadKanbanStages(data?.lead_stages)}
          />
          <KanbanBoard
            items={buyerLeads}
            getItemId={buyerLead => buyerLead.id}
            selectedItems={selectedItems}
            onItemsSelected={items =>
              setSelectedItems(items.map(item => item.toString()))
            }
            forceSelectionMode={isSelectionModeForced}
            instanceId="user-buyer-lead-kanban"
            disableReorder={buyerLeadKanbanReorderingDisabled}
            defaultPositionInNewColumn="top"
            getLiveRegionAnnouncements={args => {
              const { item } = args;
              const buyerName =
                item.lot.title ?? formatAddress(item.lot.property, ', ');

              return getBuyerLeadKanbanLiveRegionAnnouncement(args, buyerName);
            }}
            onItemMoved={(item, columnId) => {
              if (data.buyer_leads == null) {
                return;
              }

              if (!hasBuyerLeadKanbanStageChanged(item, columnId)) {
                // if the item is not moved to a different stage, we don't need to update the order
                return;
              }

              setUpdateOrderError(null);

              updateLeadStage({
                variables: {
                  id: item.id,
                  updates: {
                    stage_id: columnId.toString(),
                  },
                },
              });
            }}
            columns={columnsDefinition}
          />
          <MutationErrorSnackbar
            error={updateOrderError}
            onClose={() => setUpdateOrderError(null)}
          />
        </Box>
      )}
      <Routes>
        <Route
          path="matching"
          element={
            <UserBuyerLeadMatchingDrawer
              userId={id ?? ''}
              excludedListingIds={buyerLeads.map(lead => lead.lot.id)}
              destinationStageId={matchedStageId}
              onClose={navigateBack}
            />
          }
        />
        <Route
          path="buyers/:buyerLeadId"
          element={
            <DrawerContainerProvider value={NO_DRAWER_CONTAINER}>
              <BuyerLeadEditDrawer
                showListingCard
                showOtherListings={false}
                onClose={navigateBack}
              />
            </DrawerContainerProvider>
          }
        />
      </Routes>
    </>
  );
};
